import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, Plus, ChevronDown, ChevronUp, Check, FileText, List, Shield, Table2, LayoutGrid, ShoppingCart, Lock, Info, Download, Loader2, Clock, CheckCircle, Users, Truck, Crosshair } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { getProducts, getCategories, getProductFacets, getProductsLastUpdated, addToCartApi, getProductsExportUrl, getProductImageUrl } from '../lib/api';
import { Toaster, toast } from 'sonner';

// ── Notes parsing: split raw notes into clean bullet items ──
const LABEL_KEYWORDS = {
  'case': 'Case',
  'display': 'Display',
  'screen': 'Screen',
  'keyboard': 'Keyboard',
  'touchpad': 'Touchpad',
  'ports': 'Ports',
  'storage': 'Storage',
  'battery': 'Battery',
  'cosmetic': 'Cosmetic',
};

function parseNotes(raw) {
  if (!raw || raw.trim() === '-') return [];
  // Split on ; • | or newlines
  const parts = raw.split(/[;•|\n]+/).map(s => s.trim()).filter(Boolean);
  // Deduplicate
  const seen = new Set();
  const unique = [];
  for (const p of parts) {
    const key = p.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(p);
    }
  }
  return unique;
}

function labelForBullet(text) {
  const lower = text.toLowerCase();
  for (const [keyword, label] of Object.entries(LABEL_KEYWORDS)) {
    if (lower.startsWith(keyword + ' ') || lower.startsWith(keyword + ' -')) {
      // Extract the detail after "Keyword - "
      const idx = text.indexOf('-');
      if (idx > -1) {
        return { label, detail: text.slice(idx + 1).trim() };
      }
    }
  }
  return { label: null, detail: text };
}

const MAX_VISIBLE_BULLETS = 3;

// Table cell for Notes
const NotesCell = ({ notes }) => {
  const [expanded, setExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const bullets = parseNotes(notes);
  if (!bullets.length) return <span className="text-white/25">—</span>;

  const hasOverflow = bullets.length > MAX_VISIBLE_BULLETS;
  const visible = expanded ? bullets : bullets.slice(0, MAX_VISIBLE_BULLETS);
  const labeled = visible.map(labelForBullet);

  return (
    <div
      className="relative"
      onMouseEnter={() => !expanded && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <ul className="space-y-0.5">
        {labeled.map((b, i) => (
          <li key={i} className="flex items-start gap-1.5 text-white/55 text-[11px] leading-snug">
            <span className="text-[#00D4FF]/50 mt-[2px] shrink-0">•</span>
            {b.label ? (
              <span><span className="text-white/70 font-medium">{b.label}:</span> {b.detail}</span>
            ) : (
              <span>{b.detail}</span>
            )}
          </li>
        ))}
      </ul>
      {hasOverflow && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); setShowTooltip(false); }}
          className="text-[#00D4FF]/70 hover:text-[#00D4FF] text-[10px] mt-1 cursor-pointer"
          data-testid="notes-expand-btn"
        >
          {expanded
            ? (typeof window !== 'undefined' && document.documentElement.lang === 'es' ? 'Ver menos' : 'Show less')
            : `+${bullets.length - MAX_VISIBLE_BULLETS} more`}
        </button>
      )}
      {/* Hover tooltip preview */}
      {showTooltip && hasOverflow && !expanded && (
        <div className="absolute z-50 bottom-full left-0 mb-2 w-72 bg-[#111] border border-white/15 p-3 shadow-lg shadow-black/50" data-testid="notes-tooltip">
          <ul className="space-y-0.5">
            {bullets.map(labelForBullet).map((b, i) => (
              <li key={i} className="flex items-start gap-1.5 text-white/60 text-[11px] leading-snug">
                <span className="text-[#00D4FF]/50 mt-[2px] shrink-0">•</span>
                {b.label ? (
                  <span><span className="text-white/80 font-medium">{b.label}:</span> {b.detail}</span>
                ) : (
                  <span>{b.detail}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Grid card notes display
const NotesBullets = ({ notes }) => {
  const bullets = parseNotes(notes);
  if (!bullets.length) return null;
  const visible = bullets.slice(0, MAX_VISIBLE_BULLETS);
  const overflow = bullets.length - MAX_VISIBLE_BULLETS;
  const labeled = visible.map(labelForBullet);
  return (
    <div className="mb-4">
      <ul className="space-y-0.5">
        {labeled.map((b, i) => (
          <li key={i} className="flex items-start gap-1.5 text-white/50 text-xs leading-[1.35]">
            <span className="text-[#00D4FF]/40 mt-[3px] shrink-0">•</span>
            {b.label ? (
              <span><span className="text-white/60 font-medium">{b.label}:</span> {b.detail}</span>
            ) : (
              <span>{b.detail}</span>
            )}
          </li>
        ))}
      </ul>
      {overflow > 0 && (
        <div className="text-[#00D4FF]/50 text-[10px] mt-1">+{overflow} more</div>
      )}
    </div>
  );
};

export const ProductsPage = () => {
  const { t, addToRequest, requestList, language, isBuyerApproved, isAdmin, isBuyer, refreshCart } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [isMobile, setIsMobile] = useState(false);

  // Auto-switch to grid on small screens
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const effectiveView = isMobile ? 'grid' : viewMode;
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortField, setSortField] = useState('unique_id');
  const [sortDir, setSortDir] = useState('asc');
  const [sortOption, setSortOption] = useState('');
  const [exporting, setExporting] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const canExport = isBuyerApproved || isAdmin;

  const category = searchParams.get('category') || '';
  const grade = searchParams.get('grade') || '';
  const brand = searchParams.get('brand') || '';
  const search = searchParams.get('search') || '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes, facetsRes, lastUpdRes] = await Promise.all([
          getProducts({ category, grade, oem: brand, search }),
          getCategories(),
          getProductFacets({ category: category || undefined, grade: grade || undefined, q: search || undefined }),
          getProductsLastUpdated(),
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
        setBrands(facetsRes.data.brands || []);
        setLastUpdated(lastUpdRes.data.last_updated);
        // Reset brand if it's no longer in the facets
        const availableBrands = (facetsRes.data.brands || []).map(b => b.name);
        if (brand && !availableBrands.includes(brand)) {
          updateFilter('brand', '');
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [category, grade, brand, search]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const handleAddToRequest = (product) => {
    addToRequest(product);
    toast.success(`${product.unique_id || product.product_id || product.supplier_sku} — ${language === 'es' ? 'agregado a cotización' : 'added to quote'}`);
  };

  const handleAddSelected = () => {
    if (selectedItems.length === 0) {
      toast.error(language === 'es' ? 'No hay artículos seleccionados' : 'No items selected');
      return;
    }
    selectedItems.forEach(id => {
      const product = products.find(p => p.id === id);
      if (product) addToRequest(product);
    });
    toast.success(`${selectedItems.length} ${t('common.items')} — ${language === 'es' ? 'agregados a cotización' : 'added to quote'}`);
    setSelectedItems([]);
  };

  const toggleSelect = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const url = getProductsExportUrl({ category, grade, oem: brand, search });
      const token = localStorage.getItem('luxortek_buyer_token') || localStorage.getItem('luxortek_admin_token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Export failed' }));
        throw new Error(err.detail || 'Export failed');
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const disposition = res.headers.get('content-disposition') || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      a.download = filenameMatch ? filenameMatch[1] : 'LUXORTEK_Inventory.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(language === 'es' ? 'Exportación descargada' : 'Export downloaded');
    } catch (err) {
      toast.error(err.message || (language === 'es' ? 'Error en exportación' : 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === sortedProducts.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(sortedProducts.map(p => p.id));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleSortOption = (val) => {
    setSortOption(val);
    if (val === 'newest') { setSortField('created_at'); setSortDir('desc'); }
    else if (val === 'grade') { setSortField('grade'); setSortDir('asc'); }
    else { setSortField('unique_id'); setSortDir('asc'); }
  };

  const handleResetFilters = () => {
    setSearchParams({});
    setSortOption('');
    setSortField('unique_id');
    setSortDir('asc');
  };

  const sortedProducts = [...products].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    // Fallback for unique_id sort — use product_id or supplier_sku when unique_id is empty
    if (sortField === 'unique_id') {
      aVal = aVal || a.product_id || a.supplier_sku || '';
      bVal = bVal || b.product_id || b.supplier_sku || '';
    }
    
    // Handle numeric fields — no price sorting in customer view
    
    // Handle RAM (extract number)
    if (sortField === 'ram') {
      aVal = parseInt(aVal) || 0;
      bVal = parseInt(bVal) || 0;
    }
    
    // Handle storage (extract number)
    if (sortField === 'hard_drive') {
      aVal = parseInt(aVal) || 0;
      bVal = parseInt(bVal) || 0;
    }
    
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const getGradeBadge = (grade) => {
    const gradeUpper = grade?.toUpperCase() || '';
    if (gradeUpper.includes('A')) return 'grade-a';
    if (gradeUpper.includes('B')) return 'grade-b';
    if (gradeUpper.includes('C')) return 'grade-c';
    return 'bg-gray-600';
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDir === 'asc' ? 
      <ChevronUp className="w-3 h-3 text-[#00D4FF]" /> : 
      <ChevronDown className="w-3 h-3 text-[#00D4FF]" />;
  };

  const trustBadges = language === 'es' ? [
    { icon: FileText, text: 'Factura comercial incluida' },
    { icon: List, text: 'Lista de seriales disponible' },
    { icon: Shield, text: 'Datos eliminados' },
    { icon: Check, text: 'Centro logístico en Miami' },
  ] : [
    { icon: FileText, text: 'Commercial invoice included' },
    { icon: List, text: 'Serial list provided' },
    { icon: Shield, text: 'Data-wiped & business ready' },
    { icon: Check, text: 'Miami export hub' },
  ];

  const trustTitle = language === 'es' 
    ? 'Equipos reacondicionados listos para exportación a Latinoamérica'
    : 'Wholesale Refurbished Business Equipment Ready for Export to Latin America';

  const availabilityText = language === 'es'
    ? 'Inventario sujeto a confirmación del proveedor. Solicite cotización para precios.'
    : 'Inventory subject to supplier confirmation. Request a quote for pricing.';

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="products-page">
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-2" data-testid="products-title">
                {t('products.title')}
              </h1>
              <p className="text-white/60 text-lg">
                {trustTitle}
              </p>
            </div>
          </div>

          {/* Trust Strip */}
          <div className="bg-[#0A0A0A] border border-white/10 p-4 mb-6">
            <div className="flex flex-wrap justify-center lg:justify-between gap-4">
              {trustBadges.map((badge, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <badge.icon className="w-4 h-4 text-[#10B981]" />
                  <span className="text-white/70">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder={language === 'es' ? 'Buscar por Único #, Marca, Modelo, CPU, RAM...' : 'Search by Unique #, Brand, Model, CPU, RAM...'}
                value={search}
                onChange={(e) => updateFilter('search', e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] text-white"
                data-testid="product-search"
              />
            </div>

            {/* View Toggle — hidden on mobile (auto-grid) */}
            <div className="hidden md:flex items-center gap-2 bg-[#0A0A0A] border border-white/10 p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 ${effectiveView === 'table' ? 'bg-[#00D4FF] text-black' : 'text-white/60 hover:text-white'}`}
                data-testid="view-table-btn"
                title="Table View"
              >
                <Table2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${effectiveView === 'grid' ? 'bg-[#00D4FF] text-black' : 'text-white/60 hover:text-white'}`}
                data-testid="view-grid-btn"
                title="Grid View"
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap gap-3 items-center">
            {/* Category */}
            <select
              value={category}
              onChange={(e) => updateFilter('category', e.target.value)}
              className="px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] text-white min-w-[160px]"
              data-testid="category-filter"
            >
              <option value="">{t('products.allCategories')}</option>
              {categories.map((cat) => (
                <option key={cat.name} value={cat.name}>
                  {cat.name} ({cat.count})
                </option>
              ))}
            </select>

            {/* Brand (constrained by Category via facets) */}
            <select
              value={brand}
              onChange={(e) => updateFilter('brand', e.target.value)}
              className="px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] text-white min-w-[140px]"
              data-testid="brand-filter"
            >
              <option value="">{t('products.allBrands')}</option>
              {brands.map((b) => (
                <option key={b.name} value={b.name}>
                  {b.name} ({b.count})
                </option>
              ))}
            </select>

            {/* Grade */}
            <select
              value={grade}
              onChange={(e) => updateFilter('grade', e.target.value)}
              className="px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] text-white min-w-[130px]"
              data-testid="grade-filter"
            >
              <option value="">{t('products.allGrades')}</option>
              <option value="A+">Grade A+</option>
              <option value="A">Grade A</option>
              <option value="B">Grade B</option>
              <option value="C">Grade C</option>
            </select>

            {/* Sort */}
            <select
              value={sortOption}
              onChange={(e) => handleSortOption(e.target.value)}
              className="px-4 py-3 bg-[#0A0A0A] border border-white/10 text-white text-sm min-w-[160px]"
              data-testid="sort-dropdown"
            >
              <option value="">{language === 'es' ? 'Ordenar por' : 'Sort by'}</option>
              <option value="newest">{language === 'es' ? 'Más recientes' : 'Newest'}</option>
              <option value="grade">{language === 'es' ? 'Grado (A+ → C)' : 'Grade (A+ → C)'}</option>
            </select>

            {/* Reset */}
            {(category || brand || grade || search || sortOption) && (
              <button
                onClick={handleResetFilters}
                className="px-4 py-3 text-sm text-red-400 border border-red-400/30 hover:bg-red-400/10 transition-colors"
                data-testid="reset-filters-btn"
              >
                {language === 'es' ? 'Limpiar' : 'Reset'}
              </button>
            )}

            {/* Add Selected to Quote */}
            {isBuyer && selectedItems.length > 0 && (
              <button
                onClick={handleAddSelected}
                className="btn-primary flex items-center gap-2"
                data-testid="add-selected-btn"
              >
                <Plus className="w-4 h-4" />
                {language === 'es' ? `Cotizar ${selectedItems.length}` : `Quote ${selectedItems.length}`}
              </button>
            )}
          </div>

          {/* Info Bar: Banner + Last Updated + Download */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-[#0A0A0A] border border-white/10 p-3" data-testid="wholesale-banner">
            <div className="flex items-center gap-3 text-xs text-white/50">
              <Info className="w-4 h-4 text-[#00D4FF] flex-shrink-0" />
              <span>{language === 'es'
                ? 'Mayoristas. Procesamiento: 1–2 sem • Entrega: 1–2 sem'
                : 'Wholesale only. Processing: 1–2 wks • Delivery: 1–2 wks'
              }</span>
              {lastUpdated && (
                <span className="flex items-center gap-1 text-white/30 ml-2">
                  <Clock className="w-3 h-3" />
                  {new Date(lastUpdated).toLocaleDateString()}
                </span>
              )}
              <span className="font-mono text-white/40">{sortedProducts.length} {language === 'es' ? 'productos' : 'products'}</span>
            </div>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="px-3 py-1.5 text-[#10B981] border border-[#10B981]/30 hover:border-[#10B981] flex items-center gap-2 text-xs transition-colors disabled:opacity-50"
              data-testid="download-inventory-btn"
            >
              {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              {language === 'es' ? 'Descargar Catálogo' : 'Download Product Catalog'}
            </button>
          </div>
        </div>

        {/* Supplier confirmation notice */}
        <div className="mb-6 px-4 py-3 bg-[#0A0A0A] border border-white/10 flex items-center gap-3" data-testid="supplier-notice">
          <Info className="w-4 h-4 text-[#00D4FF] shrink-0" />
          <span className="text-sm text-white/70">
            {availabilityText}
          </span>
        </div>

        {/* Products Display */}
        {loading ? (
          <div className="card-dark p-8 text-center">
            <div className="animate-pulse text-white/40">{t('common.loading')}</div>
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className="text-center py-16" data-testid="no-products">
            <p className="text-white/60 text-lg mb-4">{t('products.noProducts')}</p>
            <Link to="/products" className="btn-secondary">
              Clear Filters
            </Link>
          </div>
        ) : effectiveView === 'table' ? (
          /* WHOLESALE TABLE VIEW */
          <div className="card-dark overflow-hidden" data-testid="products-table">
            <div className="overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
              <table className="w-full" style={{ tableLayout: 'auto', minWidth: '1100px' }}>
                <colgroup>
                  {isBuyer && <col style={{ width: '2.5%' }} />}
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '4%' }} />
                  <col style={{ width: isBuyer ? '23%' : '23%' }} />
                </colgroup>
                <thead>
                  <tr className="bg-[#0A0A0A] border-b border-white/10">
                    {isBuyer && (
                    <th className="px-3 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === sortedProducts.length && sortedProducts.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 accent-[#00D4FF]"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    )}
                    <th 
                      className="px-2 py-3 text-left text-[11px] font-semibold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white min-w-[120px]"
                      onClick={() => handleSort('unique_id')}
                    >
                      <div className="flex items-center gap-1">
                        {language === 'es' ? 'Único #' : 'Unique #'}
                        <SortIcon field="unique_id" />
                      </div>
                    </th>
                    <th 
                      className="px-2 py-3 text-left text-[11px] font-semibold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white min-w-[80px]"
                      onClick={() => handleSort('oem')}
                    >
                      <div className="flex items-center gap-1">
                        {language === 'es' ? 'Marca' : 'Brand'}
                        <SortIcon field="oem" />
                      </div>
                    </th>
                    <th 
                      className="px-2 py-3 text-left text-[11px] font-semibold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white min-w-[160px]"
                      onClick={() => handleSort('model')}
                    >
                      <div className="flex items-center gap-1">
                        {language === 'es' ? 'Modelo' : 'Model'}
                        <SortIcon field="model" />
                      </div>
                    </th>
                    <th className="px-2 py-3 text-left text-[11px] font-semibold text-white/60 uppercase tracking-wider min-w-[120px]">
                      CPU
                    </th>
                    <th 
                      className="px-2 py-3 text-left text-[11px] font-semibold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white min-w-[60px]"
                      onClick={() => handleSort('ram')}
                    >
                      <div className="flex items-center gap-1">
                        RAM
                        <SortIcon field="ram" />
                      </div>
                    </th>
                    <th 
                      className="px-2 py-3 text-left text-[11px] font-semibold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white min-w-[80px]"
                      onClick={() => handleSort('hard_drive')}
                    >
                      <div className="flex items-center gap-1">
                        {language === 'es' ? 'Almacen.' : 'Storage'}
                        <SortIcon field="hard_drive" />
                      </div>
                    </th>
                    <th className="px-2 py-3 text-left text-[11px] font-semibold text-white/60 uppercase tracking-wider min-w-[70px]">
                      {language === 'es' ? 'Pantalla' : 'Screen'}
                    </th>
                    <th 
                      className="px-2 py-3 text-center text-[11px] font-semibold text-white/60 uppercase tracking-wider cursor-pointer hover:text-white min-w-[50px]"
                      onClick={() => handleSort('grade')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        {language === 'es' ? 'Grado' : 'Grade'}
                        <SortIcon field="grade" />
                      </div>
                    </th>
                    <th className="px-2 py-3 text-center text-[11px] font-semibold text-white/60 uppercase tracking-wider min-w-[90px]">
                      {language === 'es' ? 'Disponibilidad' : 'Availability'}
                    </th>
                    <th className="px-2 py-3 text-center text-[11px] font-semibold text-white/60 uppercase tracking-wider">
                    </th>
                    <th className="px-2 py-3 text-left text-[11px] font-semibold text-white/60 uppercase tracking-wider min-w-[200px]">
                      {language === 'es' ? 'Notas' : 'Notes'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProducts.map((product, idx) => (
                    <tr 
                      key={product.id} 
                      className={`border-b border-white/5 hover:bg-[#00D4FF]/5 transition-colors ${
                        selectedItems.includes(product.id) ? 'bg-[#00D4FF]/10' : ''
                      }`}
                      style={{ verticalAlign: 'top' }}
                      data-testid={`product-row-${product.id}`}
                    >
                      {isBuyer && (
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(product.id)}
                          onChange={() => toggleSelect(product.id)}
                          className="w-4 h-4 accent-[#00D4FF] mt-0.5"
                          data-testid={`select-${product.id}`}
                        />
                      </td>
                      )}
                      <td className="px-2 py-3" style={{ wordBreak: 'break-all' }}>
                        <div className="flex items-center gap-2">
                          {product.image_url ? (
                            <img src={getProductImageUrl(product.id)} alt="" className="w-8 h-8 object-cover border border-white/10 shrink-0" />
                          ) : (
                            <div className="w-8 h-8 bg-white/5 border border-white/10 shrink-0" />
                          )}
                          <span className="font-mono text-[#00D4FF] font-semibold text-xs whitespace-nowrap">{product.unique_id || product.product_id || product.supplier_sku || '—'}</span>
                        </div>
                      </td>
                      <td className="px-2 py-3">
                        <span className="text-white font-medium text-xs">{product.oem}</span>
                      </td>
                      <td className="px-2 py-3" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <span className="text-white/90 text-xs leading-tight">{product.model}</span>
                      </td>
                      <td className="px-2 py-3" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <span className="text-white/70 text-[11px] font-mono leading-tight">{product.cpu || '-'}</span>
                      </td>
                      <td className="px-2 py-3">
                        <span className="text-white/70 text-xs font-mono">{product.ram || '-'}</span>
                      </td>
                      <td className="px-2 py-3" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <span className="text-white/70 text-xs font-mono">{product.hard_drive || '-'}</span>
                      </td>
                      <td className="px-2 py-3">
                        <span className="text-white/70 text-xs">{product.configuration || product.screen || '-'}</span>
                      </td>
                      <td className="px-2 py-3 text-center">
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          {product.grade && (
                            <span className={`px-1.5 py-0.5 text-[10px] font-bold text-white ${getGradeBadge(product.grade)}`}>
                              {product.grade}
                            </span>
                          )}
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[#10B981]/20 text-[#10B981]">
                            EXP
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-3 text-center" data-testid={`availability-${product.id}`}>
                        {(() => {
                          // If product is explicitly sold/unavailable, show no availability claim
                          const st = (product.status || '').toLowerCase();
                          if (st === 'sold' || st === 'unavailable') {
                            return <span className="text-white/30 text-[10px]">—</span>;
                          }
                          // Freshness-qualified availability display
                          const supplierDate = product.supplier_availability_date;
                          if (!supplierDate) {
                            return <span className="text-[#F59E0B] text-[10px] font-medium">{language === 'es' ? 'Consultar' : 'Inquire'}</span>;
                          }
                          const daysSince = Math.floor((Date.now() - new Date(supplierDate).getTime()) / (1000 * 60 * 60 * 24));
                          if (daysSince <= 14) {
                            const qty = product.units_available || product.quantity || 1;
                            return (
                              <div>
                                <span className="text-[#10B981] text-[10px] font-bold">{qty}</span>
                                <div className="text-[8px] text-white/30">{daysSince}d ago</div>
                              </div>
                            );
                          }
                          return <span className="text-[#F59E0B] text-[10px] font-medium">{language === 'es' ? 'Consultar' : 'Inquire'}</span>;
                        })()}
                      </td>
                      <td className="px-1 py-3 text-center">
                        <button
                          onClick={() => handleAddToRequest(product)}
                          className="p-1 bg-[#00D4FF] text-black hover:bg-[#00A0FF] transition-colors"
                          data-testid={`add-to-request-${product.id}`}
                          title={language === 'es' ? 'Agregar a cotización' : 'Add to Quote'}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </td>
                      <td className="px-2 py-3" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', verticalAlign: 'top' }}>
                        <NotesCell notes={product.damages_notes} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Table Footer - Trust Badges */}
            <div className="p-4 bg-[#050505] border-t border-white/10">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-xs text-white/50">
                    <Shield className="w-4 h-4 text-[#10B981]" />
                    <span>{language === 'es' ? 'Socios reacondicionadores certificados' : 'Certified refurbisher partners'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/50">
                    <CheckCircle className="w-4 h-4 text-[#10B981]" />
                    <span>{language === 'es' ? 'Datos borrados y probados' : 'Data-wiped & tested'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/50">
                    <FileText className="w-4 h-4 text-[#10B981]" />
                    <span>{language === 'es' ? 'Cotización personalizada por solicitud' : 'Custom quote per request'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/50">
                    <Truck className="w-4 h-4 text-[#10B981]" />
                    <span>{language === 'es' ? 'Hub de exportación Miami' : 'Miami export hub'}</span>
                  </div>
                </div>
                <div className="text-white/40 text-sm">
                  {sortedProducts.length} {language === 'es' ? 'productos' : 'products'}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* GRID VIEW (Secondary) */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
            {sortedProducts.map((product) => (
              <div
                key={product.id}
                className="card-dark p-6 group"
                data-testid={`product-card-${product.id}`}
              >
                {/* Product Image */}
                {product.image_url && (
                  <div className="mb-3 aspect-[4/3] overflow-hidden border border-white/10">
                    <img src={getProductImageUrl(product.id)} alt={product.model || ''} className="w-full h-full object-cover" />
                  </div>
                )}

                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-[#00D4FF] font-mono font-semibold mb-1">{product.unique_id || product.product_id || product.supplier_sku || '—'}</div>
                    <div className="text-white/80 text-sm">{product.oem}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {product.grade && (
                      <span className={`px-2 py-1 text-xs font-bold text-white ${getGradeBadge(product.grade)}`}>
                        {product.grade}
                      </span>
                    )}
                    <span className="px-2 py-1 text-xs font-medium bg-[#10B981]/20 text-[#10B981]">
                      EXPORT
                    </span>
                  </div>
                </div>

                {/* Model */}
                <h3 className="text-lg font-semibold mb-4 line-clamp-2">{product.model}</h3>

                {/* Specs */}
                <div className="space-y-2 mb-4 text-sm text-white/60">
                  {product.cpu && (
                    <div className="flex justify-between">
                      <span>CPU</span>
                      <span className="text-white/80 font-mono text-xs truncate max-w-[60%]">{product.cpu}</span>
                    </div>
                  )}
                  {product.ram && (
                    <div className="flex justify-between">
                      <span>RAM</span>
                      <span className="text-white/80 font-mono text-xs">{product.ram}</span>
                    </div>
                  )}
                  {product.hard_drive && (
                    <div className="flex justify-between">
                      <span>Storage</span>
                      <span className="text-white/80 font-mono text-xs">{product.hard_drive}</span>
                    </div>
                  )}
                  {product.screen && (
                    <div className="flex justify-between">
                      <span>Screen</span>
                      <span className="text-white/80 font-mono text-xs">{product.screen}</span>
                    </div>
                  )}
                </div>

                {/* Availability Status */}
                <div className="mb-4 px-3 py-2 border" data-testid={`availability-grid-${product.id}`}>
                  {(() => {
                    // If product is explicitly sold/unavailable, show no availability claim
                    const st = (product.status || '').toLowerCase();
                    if (st === 'sold' || st === 'unavailable') {
                      return (
                        <div className="bg-white/5 border-white/10">
                          <span className="text-white/30 text-xs">—</span>
                        </div>
                      );
                    }
                    const supplierDate = product.supplier_availability_date;
                    if (!supplierDate) {
                      return (
                        <div className="bg-[#F59E0B]/10 border-[#F59E0B]/20">
                          <span className="text-[#F59E0B] text-xs font-medium">
                            {language === 'es' ? 'Consultar disponibilidad actual' : 'Request current availability'}
                          </span>
                        </div>
                      );
                    }
                    const daysSince = Math.floor((Date.now() - new Date(supplierDate).getTime()) / (1000 * 60 * 60 * 24));
                    if (daysSince <= 14) {
                      const qty = product.units_available || product.quantity || 1;
                      return (
                        <div className="bg-[#10B981]/10 border-[#10B981]/20">
                          <span className="text-[#10B981] text-xs font-bold">{qty} {language === 'es' ? 'disponibles' : 'available'}</span>
                          <span className="text-white/30 text-[10px] ml-2">({daysSince}d ago)</span>
                        </div>
                      );
                    }
                    return (
                      <div className="bg-[#F59E0B]/10 border-[#F59E0B]/20">
                        <span className="text-[#F59E0B] text-xs font-medium">
                          {language === 'es' ? 'Consultar disponibilidad actual' : 'Request current availability'}
                        </span>
                      </div>
                    );
                  })()}
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="text-xs px-2 py-1 bg-white/5 text-white/50">Invoice</span>
                  <span className="text-xs px-2 py-1 bg-white/5 text-white/50">{t('productsPage.serialList')}</span>
                  <span className="text-xs px-2 py-1 bg-white/5 text-white/50">{t('productsPage.dataWiped')}</span>
                </div>

                {/* Notes */}
                {product.damages_notes && (
                  <NotesBullets notes={product.damages_notes} />
                )}

                {/* Action */}
                <div className="pt-4 border-t border-white/10">
                  <button
                    onClick={() => handleAddToRequest(product)}
                    className="btn-primary w-full flex items-center justify-center gap-2 text-xs py-3 px-4"
                    data-testid={`add-to-request-${product.id}`}
                  >
                    <Plus className="w-4 h-4" />
                    {language === 'es' ? 'Solicitar Cotización' : 'Request Quote'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Can't Find It? We Source It */}
        <div className="mt-8 card-dark p-8 text-center" data-testid="cant-find-section">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Crosshair className="w-6 h-6 text-[#00D4FF]" />
            <h3 className="text-xl font-bold">{t('rfq.cantFindTitle')} {t('rfq.cantFindSubtitle')}</h3>
          </div>
          <p className="text-white/60 text-sm mb-6 max-w-xl mx-auto">{t('rfq.cantFindDesc')}</p>
          <Link
            to="/contact"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="custom-sourcing-btn"
          >
            {t('rfq.customSourcing')}
          </Link>
        </div>

        {/* Floating Actions Bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10 py-3 px-6 z-40 flex items-center justify-between" data-testid="floating-actions">
          <div className="flex items-center gap-3">
            {requestList.length > 0 && (
              <Link to="/quote" className="btn-primary flex items-center gap-2 text-sm" data-testid="view-quote-btn">
                <FileText className="w-4 h-4" />
                {language === 'es' ? `Ver Cotización (${requestList.length})` : `View Quote (${requestList.length})`}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Bottom spacer for floating bar */}
      <div className="h-16" />
    </div>
  );
};
